import { useEffect, useState, useRef } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    TextField,
    Avatar, Tooltip,
    Grid, CircularProgress,
    Button, IconButton, MenuItem, Select, Divider, Menu, Stack
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from "react-redux";
import { setActiveBack, setTemplateFlow } from "../templatesSlice";
import SnackbarMessage from "../../../components/snackBar/snackBar";
import CircularLoader from "../../../components/loaders/circularLoader";
import { uploadFileThunk } from "../../../components/general/generalThunk";
import { createTemplateThunk, getTemplateThunk } from "../templatesThunk";
import { APP_ROUTES } from "../../../config/appRoutes";
import { useNavigate } from "react-router-dom";
import PhoneIcon from "@mui/icons-material/Phone";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ChatIcon from '@mui/icons-material/Chat';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import TitleIcon from '@mui/icons-material/Title';
import NotesIcon from '@mui/icons-material/Notes';
import ShortTextIcon from '@mui/icons-material/ShortText';
import SmartButtonIcon from '@mui/icons-material/SmartButton';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import convoBg from '../../../assests/convoBg.jpg';
import { stepCardSx, stepActionsSx, backBtnSx, nextBtnSx, StepHeader } from "../../campaign/components/stepUi";


const headerTypes = [{ value: 0, label: "None" }, { value: 1, label: "Text" }, { value: 2, label: "Image" }, { value: 3, label: "Video" }, { value: 4, label: "Document" }]
const actionTypes = [{ value: 2, label: "Call Phone Number" }, { value: 1, label: "Visit Website" }, { value: 3, label: "Quick Reply" }];
const urlTypes = [{ value: 1, label: "Static" }];
const countryCodes = [{ value: 1, label: "+91" }];

// ------------------- Flow layout helpers (presentation only) -------------------
const ACCENT = "#2563eb";

const BTN_META = {
    "1": { label: "Visit Website", icon: <OpenInNewIcon />, color: ACCENT },
    "2": { label: "Call Phone Number", icon: <PhoneIcon />, color: "#16a34a" },
    "3": { label: "Quick Reply", icon: <ChatIcon />, color: "#7c3aed" },
};

const softBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: 13,
    borderRadius: 2,
    px: 1.25,
    py: 0.5,
    color: ACCENT,
    bgcolor: `${ACCENT}0f`,
    "&:hover": { bgcolor: `${ACCENT}1f` },
    "& .MuiButton-startIcon svg": { fontSize: 18 },
};
const softBtnOffSx = { opacity: 0.5, cursor: "not-allowed", "&:hover": { bgcolor: `${ACCENT}0f` } };

const counterSx = { fontSize: 12, fontWeight: 600, color: "#94a3b8" };

const pillRadioSx = {
    m: 0,
    pl: 0.5,
    pr: 1.5,
    height: 36,
    flexShrink: 0,
    borderRadius: 5,
    border: "1.5px solid #e2e8f0",
    bgcolor: "#fff",
    transition: "all .15s ease",
    "& .MuiRadio-root": { p: 0.75 },
    "& .MuiRadio-root .MuiSvgIcon-root": { fontSize: 18 },
    "& .MuiFormControlLabel-label": { fontSize: 13.5, fontWeight: 600, color: "#475569" },
    "&:hover": { borderColor: "#cbd5e1" },
    "&:has(.Mui-checked)": {
        borderColor: ACCENT,
        bgcolor: `${ACCENT}0d`,
        "& .MuiFormControlLabel-label": { color: ACCENT, fontWeight: 700 },
    },
};

const sampleBoxSx = { mt: 2, p: { xs: 1.25, sm: 1.5 }, borderRadius: 2.5, bgcolor: "#f8fafc", border: "1px dashed #cbd5e1" };
const sampleTitleSx = { fontSize: 13, fontWeight: 700, color: "#0f172a" };
const sampleTextSx = { fontSize: 12, color: "#64748b", lineHeight: 1.5 };

const varChipSx = {
    flexShrink: 0,
    minWidth: 54,
    textAlign: "center",
    px: 1,
    py: 0.75,
    borderRadius: 2,
    fontFamily: "monospace",
    fontSize: 12.5,
    fontWeight: 700,
    color: "#7c3aed",
    bgcolor: "#7c3aed12",
    border: "1px solid #7c3aed26",
};

const removeBtnSx = { flexShrink: 0, width: 34, height: 34, borderRadius: 2, color: "#94a3b8", "&:hover": { color: "#dc2626", bgcolor: "#fef2f2" } };

const btnIconSx = { width: 30, height: 30, flexShrink: 0, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 17 } };

function FieldLabel({ children }) {
    return (
        <Typography sx={{ display: "block", mb: 0.75, fontSize: 12, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", color: "#64748b" }}>
            {children}
        </Typography>
    );
}

// Section card: icon + title (+ Optional / *) + hint, actions on the right
function FlowSection({ icon, title, optional, subtitle, right, error, children }) {
    return (
        <Box sx={{ border: "1px solid #e9edf2", borderRadius: 3, bgcolor: "#fff", overflow: "hidden" }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1.5}
                sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, bgcolor: "#f8fafc", borderBottom: "1px solid #e9edf2" }}
            >
                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
                    <Box sx={{ ...btnIconSx, width: 32, height: 32, color: ACCENT, bgcolor: `${ACCENT}14`, "& svg": { fontSize: 18 } }}>{icon}</Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{title}</Typography>
                            {optional ? (
                                <Box component="span" sx={{ px: 0.75, py: 0.1, borderRadius: 1, fontSize: 10.5, fontWeight: 700, color: "#64748b", bgcolor: "#eef2f6" }}>
                                    Optional
                                </Box>
                            ) : (
                                <Box component="span" sx={{ color: "#ef4444", fontWeight: 700 }}>*</Box>
                            )}
                        </Stack>
                        <Typography sx={{ fontSize: 12, color: "#64748b", lineHeight: 1.4 }}>{subtitle}</Typography>
                    </Box>
                </Stack>
                {right}
            </Stack>
            {error && (
                <Box sx={{ px: 2, py: 0.75, fontSize: 12.5, fontWeight: 600, color: "#dc2626", bgcolor: "#fef2f2", borderBottom: "1px solid #fecaca" }}>
                    {error}
                </Box>
            )}
            <Box sx={{ p: { xs: 1.5, sm: 2 } }}>{children}</Box>
        </Box>
    );
}

export default function TemplateCreationFlow() {
    const navigate = useNavigate();
    const scrollRef = useRef(null);
    const { templateNameData, activeStep, templateFlowPreview } = useSelector((state) => state.templates);
    const { status, error } = useSelector((state) => state.general);
    const loader = useSelector((state) => state.templates.status);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const dispatch = useDispatch();
    const [headerType, setHeaderType] = useState("0");
    const [selectedFile, setSelectedFile] = useState("");
    const [headTextError, setHeadTextError] = useState("");
    const [sampleHeadError, setSampleHeadError] = useState("");
    const [bodyError, setBodyError] = useState("");
    const [subBodyError, setSubBodyError] = useState("");
    const [buttonError, setButtonError] = useState("");
    const [footerText, setFooterText] = useState("");
    const [headText, setHeadText] = useState("");
    const [headTextPreview, setHeadTextPerview] = useState("");
    const [headVariableCounter, setHeadVariableCounter] = useState(1);
    const [headVariableFields, setHeadVariableFields] = useState([]);
    const [headVariableValues, setHeadVariableValues] = useState([]);
    const [bodyText, setBodyText] = useState("");
    const [bodyTextPreview, setBodyTextPerview] = useState("");
    const [bodyVariableCount, setBodyVariableCount] = useState(1);
    const [bodyVariableFields, setBodyVariableFields] = useState([]);
    const [bodyVariableValues, setBodyVariableValues] = useState([]);
    const [buttons, setButtons] = useState([]);
    const bodyTextAreaRef = useRef(null);
    const headTextAreaRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };



    useEffect(() => {
        const replacedText = headText.replace('{{1}}', ` <b>${headVariableValues}</b> `);
        setHeadTextPerview(replacedText);
        setBodyError("");
        setHeadTextError("");
        setSampleHeadError("");
        setBodyError("");
        setSubBodyError("");
        setButtonError("");
    }, [headText, headVariableValues]);

    useEffect(() => {
        const modifiedBodyText = bodyText.replace(/\n/g, '<br>').replace(/ \*(\S*?)\* /g, ' <b>$1</b> ');
        const result = replacePlaceholders(modifiedBodyText, bodyVariableValues);
        setBodyTextPerview(result);
        setBodyError("");
        setHeadTextError("");
        setSampleHeadError("");
        setBodyError("");
        setSubBodyError("");
        setButtonError("");
    }, [bodyText, bodyVariableValues]);


    const handleFileChange = async (e) => {
        // reset states
        setHeadText("");
        setHeadTextPerview("");
        setHeadVariableCounter(1);
        setHeadVariableFields([]);
        setHeadVariableValues([]);
        setSelectedFile(null);
        setBodyError("");
        setHeadTextError("");
        setSampleHeadError("");
        setSubBodyError("");
        setButtonError("");

        const file = e.target.files?.[0];

        if (!file) {
            console.error("No file selected.");
            return;
        }

        // ✅ size validation (15 MB = 15 * 1024 * 1024 bytes)
        const MAX_FILE_SIZE = 15 * 1024 * 1024;

        // add small prefix size overhead (base64 encoding ~33% larger)
        const estimatedSize = Math.ceil(file.size * 1.37); // safe multiplier

        if (estimatedSize > MAX_FILE_SIZE) {
            return setSnackbar({
                open: true,
                message: "File size exceeds 15MB limit. Please upload a smaller file.",
                severity: "error",
            });
        }

        // ✅ Read base64 for API
        const reader = new FileReader();
        const fileName = file.name;

        reader.onloadend = async () => {
            const base64Data = reader.result; // full data URI: data:image/png;base64,...
            const templateFile = [{ filename: fileName, img: base64Data }];

            let uploadResult;

            try {
                uploadResult = await dispatch(
                    uploadFileThunk({ credentials: { file: templateFile, category: "template" } })
                );
            } catch (error) {
                console.error("Upload failed:", error);
                return setSnackbar({
                    open: true,
                    message: "File upload failed",
                    severity: "error",
                });
            }

            if (
                uploadResult.meta.requestStatus !== "fulfilled" ||
                !uploadResult.payload?.data?.length
            ) {
                return setSnackbar({
                    open: true,
                    message: uploadResult.payload?.message || "File upload failed",
                    severity: "error",
                });
            }

            const fileUrl = uploadResult.payload.data[0].file_url;
            setSelectedFile(fileUrl);
        };

        reader.readAsDataURL(file);
    };


    const handleHeaderType = (e, value) => {
        const selectedValue = value;

        setHeaderType(selectedValue);
        setHeadText("");
        setHeadTextPerview("");
        setHeadVariableCounter(1);
        setHeadVariableFields([]);
        setHeadVariableValues([]);
        setSelectedFile("");
        setBodyError("");
        setHeadTextError("");
        setSampleHeadError("");
        setBodyError("");
        setSubBodyError("");
        setButtonError("");
    };


    const handleFooterChange = (e) => {
        const inputText = e.target.value.replace(/[^A-Za-z0-9_\s]/g, '');
        if (inputText.length <= 60) {
            setFooterText(inputText);
        }
    };

    // ADD BODY VARIABLE
    const addBodyVariableFun = () => {
        setSubBodyError("");
        const textarea = bodyTextAreaRef.current;
        const startPos = textarea.selectionStart;
        const endPos = textarea.selectionEnd;
        const variable = `{{${bodyVariableFields.length + 1}}}`;

        const newText =
            textarea.value.substring(0, startPos) +
            variable +
            textarea.value.substring(endPos, textarea.value.length);

        setBodyText(newText);
        setBodyError("");
        setHeadTextError("");
        setSampleHeadError("");
        setBodyError("");
        setSubBodyError("");
        setButtonError("");
        setBodyVariableFields((prevVariables) => [...prevVariables, variable]);
        setBodyVariableCount((prevCounter) => prevCounter + 1);
        setBodyVariableValues((prevstate) => {
            return [...prevstate, ""];
        });

        textarea.selectionStart = startPos + variable.length;
        textarea.selectionEnd = startPos + variable.length;
        textarea.focus();
    };

    const handleBodyChange = (e) => {

        // const inputText = e.target.value.replace(/\n{1,}/g, "\n");
        let inputText = e.target.value;

        inputText = inputText.replace(/^\s+/, "");

        // Replace multiple consecutive newlines with a single newline
        // inputText = inputText.replace(/\n{2,}/g, "\n");
        inputText = inputText.replace(/\n\s*\n\s*\n+/g, "\n\n");

        inputText = inputText.replace(/^[ ]+$/gm, "");

        // Prevent input that's only spaces/newlines
        if (/^[\s\n]*$/.test(inputText)) {
            inputText = "";
        }

        if (inputText.length <= 1024) {
            setBodyText(inputText);

            const placeholders = inputText.match(/{{\d+}}/g);
            if (placeholders !== null) {
                setBodyVariableFields([...new Set(placeholders)]);
            } else {
                setBodyVariableFields([]);
                setBodyVariableValues([]);
            }
        }
        if (inputText.length === 0) {
            setBodyVariableCount(1);
            setBodyVariableFields([]);
            setBodyVariableValues([]);
            setBodyText(inputText);
        }
        setBodyError("");
        setHeadTextError("");
        setSampleHeadError("");
        setBodyError("");
        setSubBodyError("");
        setButtonError("");
    };

    // ADD HEAD VARIABLE
    const addHeadVariableFun = () => {

        const textarea = headTextAreaRef.current;
        const startPos = textarea.selectionStart;
        const endPos = textarea.selectionEnd;
        const variable = `{{${headVariableCounter}}}`;

        if (textarea.value.length + variable.length > 60) {
            setHeadTextError("Maximum 60 characters allowed")
            return;
        };

        const newText =
            textarea.value.substring(0, startPos) +
            variable +
            textarea.value.substring(endPos, textarea.value.length);
        setHeadText(newText);
        setHeadTextPerview(newText);
        setHeadVariableFields((prevVariables) => [...prevVariables, variable]);
        setHeadVariableCounter((prevCounter) => prevCounter);
        setBodyError("");
        setHeadTextError("");
        setSampleHeadError("");
        setBodyError("");
        setSubBodyError("");
        setButtonError("");

        textarea.selectionStart = startPos + variable.length;
        textarea.selectionEnd = startPos + variable.length;
        textarea.focus();
    };


    // header input text
    const handleHeadChange = (e) => {
        const inputText = e.target.value.replace(/[^A-Za-z0-9_\s]/g, '');
        // let inputText = e.target.value;
        // inputText = inputText.replace(/^\s+/, "");
        // inputText = inputText.replace(/\n{2,}/g, "\n");
        // inputText = inputText.replace(/[^A-Za-z0-9_\s]/g, '');
        // if (/^[\s\n]*$/.test(inputText)) {
        //     inputText = "";
        // }

        if (inputText.length <= 60) {
            setHeadText(inputText);
            setHeadTextPerview(inputText);
        }

        if (inputText.length === 0) {
            setHeadVariableFields([]);
            setHeadVariableValues([]);
        }

        if (inputText) {
            if (inputText.includes("{{1}}")) {
                if (headVariableFields.length === 0) {
                    setHeadVariableFields(["{{1}}"]);
                }
            } else {
                setHeadVariableFields([]);
                setHeadVariableValues([]);
            }
        }

        setBodyError("");
        setHeadTextError("");
        setSampleHeadError("");
        setBodyError("");
        setSubBodyError("");
        setButtonError("");
    };

    // add body varibles
    const addBodyVariableValuesFun = (e, index) => {
        const value = e.target.value;
        console.log("value", value);

        setBodyError("");
        setHeadTextError("");
        setSampleHeadError("");
        setBodyError("");
        setSubBodyError("");
        setButtonError("");
        // setBodyVariableValues((prevstate) => {
        //     const updatedData = prevstate.map((item, itemIndex) => {
        //         if (index === itemIndex) {
        //             return value;
        //         }
        //         return item;
        //     });
        //     return updatedData;
        // });


        setBodyVariableValues((prevstate) => {
            const updatedData = [...prevstate];
            updatedData[index] = value;
            return updatedData;
        });
    };


    const addHeadVariableValuesFun = (e, item) => {
        const newVariableText = e.target.value.replace(/[^\w ]/g, '').replace(/[^A-Za-z0-9_\s]/g, '');
        setHeadVariableValues(newVariableText);
        if (newVariableText.length < 1) {
            setSampleHeadError("Please enter Sample head content");
        } else {
            setBodyError("");
            setHeadTextError("");
            setSampleHeadError("");
            setBodyError("");
            setSubBodyError("");
            setButtonError("");
        }
    };

    function replacePlaceholders(str, values) {
        const placeholders = str.match(/{{\d+}}/g);
        if (!placeholders) {
            console.log("No placeholders found.");
            return str;
        }
        const uniquePlaceholders = [...new Set(placeholders)];

        let replacedStr = str;

        uniquePlaceholders.forEach((placeholder, index) => {
            if (index < values.length) {
                const regex = new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
                replacedStr = replacedStr.replace(regex, `<b>${values[index]}</b>`);
            } else {
                console.log(`No value provided for placeholder ${placeholder}`);
            }
        });

        return replacedStr;
    }

    const renderInputBasedOnFileType = () => {
        const accept = headerType === "2" ? "image/*" : headerType === "3" ? "video/*" : headerType === "4" ? ".pdf" : undefined;
        const kind = headerType === "2" ? "image" : headerType === "3" ? "video" : headerType === "4" ? "PDF document" : "file";
        const hasFile = Boolean(selectedFile && selectedFile.length > 0);
        return (
            <Box
                component="label"
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    cursor: "pointer",
                    borderRadius: 2.5,
                    border: `1.5px dashed ${hasFile ? "#86efac" : "#c7d7f5"}`,
                    bgcolor: hasFile ? "#f0fdf4" : "#f8fbff",
                    transition: "all .15s ease",
                    "&:hover": { borderColor: hasFile ? "#16a34a" : ACCENT, bgcolor: hasFile ? "#ecfdf5" : "#eff6ff" },
                }}
            >
                <Box sx={{ ...btnIconSx, width: 40, height: 40, color: hasFile ? "#16a34a" : ACCENT, bgcolor: hasFile ? "#16a34a14" : `${ACCENT}14`, "& svg": { fontSize: 22 } }}>
                    {hasFile ? <CheckCircleOutlineIcon /> : <CloudUploadOutlinedIcon />}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a" }}>
                        {hasFile ? `${kind.charAt(0).toUpperCase() + kind.slice(1)} uploaded` : `Click to upload ${kind}`}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                        {hasFile ? "Click to choose a different file" : "The preview on the right updates after upload"}
                    </Typography>
                </Box>
                <input type="file" hidden accept={accept} onChange={handleFileChange} />
            </Box>
        );
    };



    const handleOptionClick = (option) => {

        if (option === "1" && buttons.filter(button => button.button_type === "1").length >= 2) {
            alert("You can add only two visit website buttons");
            return;
        }

        if (option === "2" && buttons.filter(button => button.button_type === "2").length >= 1) {
            alert("You can add only one phone number button");
            return;
        }
        if (option === "3" && buttons.filter(button => button.button_type === "3").length >= 6) {
            alert("You can add only one phone number button");
            return;
        }
        const newButton = {
            button_type: option,
            button_text: option === "1" ? "Visit website" : option === "2" ? "Call phone number" : "Quick Reply",
            type_of_action: option,
            country_code: "1",
            url_type: "1",
            phone_number: "",
            website_url: ""
        };
        setButtons((prevState) => [...prevState, newButton]);
    };


    const handlePaste = (e) => {
        // Get the pasted content
        const pastedText = e.clipboardData.getData('text/plain');

        // Prevent the default paste behavior
        e.preventDefault();

        // Replace newline characters with <br> tags and wrap bold text with <strong> tags
        const processedText = pastedText.replace(/\n/g, '<br>').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

        // Insert the processed content at the cursor position
        const { selectionStart, selectionEnd } = headTextAreaRef.current;
        const newValue =
            headText.substring(0, selectionStart) + processedText + headText.substring(selectionEnd);
        setHeadText(newValue);

        // Move the cursor to the end of the pasted content
        setTimeout(() => {
            headTextAreaRef.current.selectionStart = headTextAreaRef.current.selectionEnd =
                selectionStart + processedText.length;
        }, 0);
    };


    const handleClose = (index) => {
        setButtons((prevstate) => prevstate.filter((_, i) => i !== index));
    };

const handleAddButtonChange = (e, index) => {
        let value = e.target.value
        const name = e.target.name;
        value = value.replace(/^\s+/, "");

        if (name === "phone_number") {
            value = value.replace(/[^0-9]/g, '');
            if (value.length > 10) return;
        } else if (name === "button_name") {
            value = value.replace(/[^A-Za-z0-9\s]/g, '').slice(0, 50);
        } else {
            value = value.replace(/[^A-Za-z0-9.:/]/g, '').slice(0, 50);
        }


        setButtons((prevstate) =>
            prevstate.map((item, i) => {
                if (i === index) {
                    if (name === "button_name") {
                        return { ...item, button_text: value };
                    } else if (name === "website_url") {
                        return { ...item, website_url: value };
                    } else if (name === "phone_number") {
                        if (value.length <= 10) {
                            return { ...item, phone_number: value };
                        }
                    }
                }
                return item;
            })
        );
    };

    const processHTMLContent = (text) => {
        const processedText = text.replace(/\n/g, '<br>').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\*(.*?)\*/g, "<strong>$1</strong>").replace(/_(.*?)_/g, "<em>$1</em>");
        return { __html: processedText };
    };

    const handleTemplateSubmit = async (e) => {
        e.preventDefault();
        // HEADER validation
        if (String(headerType) === "1") {
            if (!headText?.trim()) {
                setHeadTextError("Please enter header content");
                setSnackbar({ open: true, message: "Please enter header content*", severity: "error" });
                return false;
            }
            if (headVariableFields?.length > 0 && headVariableValues.length === 0) {
                setHeadTextError("Please enter header variables*");
                setSnackbar({ open: true, message: "Please enter header variables*", severity: "error" });
                return false;
            }
        }


        if (["2", "3", "4"].includes(headerType)) {
            if (selectedFile === "") {
                setSnackbar({ open: true, message: "Please select sample file*", severity: "error" });
                return false;
            }
        }

        // BODY validation
        if (bodyText === "") {
            setBodyError("Please enter body content*")
            setSnackbar({ open: true, message: "Please enter body content*", severity: "error" });
            return false;
        }

        // if (bodyVariableFields.length > 0) {
        //     bodyVariableValues.length = bodyVariableFields.length;
        //     const isEmpty = bodyVariableValues.some(item => item === "");
        //     if (isEmpty) {
        //         setSnackbar({ open: true, message: "Please enter body variables*", severity: "error" });
        //         return false;
        //     }
        // }

        if (bodyVariableFields.length > 0) {
            const values = [...bodyVariableValues]; // safe copy

            // Adjust length safely (don’t mutate state directly)
            while (values.length < bodyVariableFields.length) values.push("");
            while (values.length > bodyVariableFields.length) values.pop();

            const isEmpty = values.some((item) => item.trim() === "");
            if (isEmpty) {
                setSnackbar({
                    open: true,
                    message: "Please enter body variables*",
                    severity: "error",
                });
                return false;
            }
        }


        // BUTTONS validation
        if (buttons.length > 0) {
            let buttonTexts = new Set();
            for (let item of buttons) {
                if (item.button_type === "2") {
                    if (item.button_text === "" || item.phone_number === "" || item.phone_number.length < 10) {
                        setSnackbar({ open: true, message: "Please enter valid phone button details", severity: "error" });
                        return false;
                    }
                } else if (item.button_type === "1") {
                    if (item.button_text === "" || item.website_url === "") {
                        setSnackbar({ open: true, message: "Please enter valid URL button details", severity: "error" });
                        return false;
                    }
                    if (buttonTexts.has(item.button_text)) {
                        setSnackbar({ open: true, message: "Button text must be unique", severity: "error" });
                        return false;
                    }
                    buttonTexts.add(item.button_text);
                }
            }
        }

        let template_form = [];
        let headerObject = {};
        let footerObject = {};
        let buttonsObject = {};
        let type = "";

        // if (headerType === "0" || headerType === "1") type = "text"
        if (headerType === "2") type = "image/png";
        else if (headerType === "3") type = "video/mp4";
        else if (headerType === "4") type = "application/pdf";


        if (headerType === "1") {
            headerObject = {
                type: "HEADER",
                format: "TEXT",
                text: headText,
                example: headVariableValues.length > 0 ? { header_text: [headVariableValues] } : {},
            };
            template_form.push(headerObject);
        } else if ([2, 3, 4].includes(Number(headerType))) {
            headerObject = {
                type: "HEADER",
                format: headerType === "2" ? "image" : headerType === "3" ? "video" : "document",
                example: { header_handle: [selectedFile] },
            };
            template_form.push(headerObject);
        }

        let bodyObject = {
            type: "BODY",
            text: bodyText,
            example: bodyVariableValues.length > 0 ? { body_text: [bodyVariableValues] } : {},
        };
        template_form.push(bodyObject);

        if (footerText !== "") {
            footerObject = { type: "FOOTER", text: footerText };
            template_form.push(footerObject);
        }

        if (buttons.length > 0) {
            let buttonsData = [];
            buttons.forEach((item) => {
                if (item.button_type === "2") {
                    buttonsData.push({ type: "PHONE_NUMBER", text: item.button_text, phone_number: `91${item.phone_number}` });
                } else if (item.button_type === "1") {
                    buttonsData.push({ type: "url", text: item.button_text, url: item.website_url });
                } else if (item.button_type === "3") {
                    buttonsData.push({ type: "QUICK_REPLY", text: item.button_text });
                }
            });
            buttonsObject = { type: "BUTTONS", buttons: buttonsData };
            template_form.push(buttonsObject);
        }

        console.log("template_form", JSON.stringify(template_form));

        try {
            const result = await dispatch(
                createTemplateThunk({
                    credentials: {
                        "template_name": templateNameData[0]?.templateName ?? "-",
                        "template_category": templateNameData[0]?.categoryName ?? "-",
                        "template_lang": templateNameData[0]?.language ?? "-",
                        "type": type,
                        "template_form": JSON.stringify(template_form),
                    }
                })
            );

            if (result.meta.requestStatus === "fulfilled") {
                setSnackbar({
                    open: true,
                    message: result.payload.message,
                    severity: "success",
                });

                dispatch(getTemplateThunk({ type: "getTemplate" }));
                navigate(APP_ROUTES.TEMPLATE.LIST);
            } else {
                setSnackbar({
                    open: true,
                    message: result.payload,
                    severity: "error",
                });
            }
        } catch {
            setSnackbar({
                open: true,
                message: "Server error. Please try again.",
                severity: "error",
            });
        }

        return true;
    };

    const handleBack = () => {
        const backData = [
            {
                headerType,

                headText,
                headTextPreview,
                headVariableValues,
                headVariableFields,
                headVariableCounter,

                bodyText,
                bodyVariableFields,
                bodyVariableValues,
                bodyVariableCount,

                selectedFile,

                footerText,
                buttons
            }
        ];
        dispatch(setTemplateFlow(backData));
        dispatch(setActiveBack(activeStep - 1));
    };


    useEffect(() => {
        if (status === "failed") setSnackbar({ open: true, message: error, severity: "error" });
    }, [status, error]);


    useEffect(() => {
        if (templateFlowPreview?.length > 0) {
            const {
                headerType,
                headText,
                headVariableFields,
                headVariableValues,
                headVariableCounter,
                selectedFile,
                bodyText,
                bodyVariableFields,
                bodyVariableValues,
                bodyVariableCount,
                footerText,
                buttons
            } = templateFlowPreview[0];

            setHeaderType(headerType);
            setHeadText(headText);

            let preview = headText;
            headVariableFields.forEach((variable, idx) => {
                const value = headVariableValues[idx] || variable;
                preview = preview.replace(variable, `<b>${value}</b>`);
            });

            setHeadTextPerview(preview);
            setHeadVariableValues(headVariableValues || []);
            setHeadVariableCounter(headVariableCounter || 1);
            setSelectedFile(selectedFile);

            setBodyText(bodyText);
            setBodyVariableFields(bodyVariableFields || []);
            setBodyVariableValues(bodyVariableValues || []);
            setBodyVariableCount(bodyVariableCount || (bodyVariableFields?.length || 1));

            setFooterText(footerText);
            setButtons(buttons);
        }
    }, [templateFlowPreview]);


    const handleDeleteVariable = (indexToDelete) => {
        const placeholders = bodyText.match(/{{\d+}}/g) || [];
        const placeholderToDelete = placeholders[indexToDelete];
        setBodyText((prev) => prev.replace(placeholderToDelete, "").replace(/\s+/g, " ").trim());
        setBodyVariableFields((prev) => prev.filter((_, i) => i !== indexToDelete));
        setBodyVariableValues((prev) => prev.filter((_, i) => i !== indexToDelete));
        setBodyVariableCount((prevCounter) => Math.max(prevCounter - 1, 0));
        setSnackbar((prev) => ({ ...prev, open: true, message: `Deleted Successfully`, severity: "success" }))
    };




    // Form look inside the flow: blue accents, same-size rounded inputs
    const flowSx = {
        "& .MuiRadio-root.Mui-checked, & .MuiCheckbox-root.Mui-checked": { color: ACCENT },
        "& .MuiOutlinedInput-root": { borderRadius: 2.5, bgcolor: "#fff", fontSize: 14 },
        "& .MuiOutlinedInput-root:not(.MuiInputBase-multiline)": { minHeight: 42 },
        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb", borderWidth: "1.5px" },
        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ACCENT, borderWidth: "1.5px" },
        "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "#ef4444" },
        "& .MuiOutlinedInput-root.Mui-disabled": { bgcolor: "#f8fafc" },
        "& .MuiInputBase-input::placeholder": { color: "#94a3b8", opacity: 1 },
    };

    const previewTime = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    return (
        <Box>
            <Card sx={{ ...stepCardSx, ...flowSx }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <StepHeader
                        icon={<AccountTreeOutlinedIcon sx={{ fontSize: 20 }} />}
                        title="Template Flow"
                        subtitle="Build the header, body, footer and buttons. The preview updates as you type."
                    />

                    <Grid container spacing={{ xs: 2, md: 3 }}>
                        {/* ================= LEFT: form ================= */}
                        <Grid size={{ xs: 12, md: 7, lg: 7 }}>
                            <Stack spacing={2}>
                                {/* ---------- HEADER ---------- */}
                                <FlowSection
                                    icon={<TitleIcon />}
                                    title="Header"
                                    optional
                                    subtitle="Add a title or choose the type of media for this header."
                                >
                                    <FieldLabel>Header type</FieldLabel>
                                    <FormControl sx={{ width: "100%" }}>
                                        <Box
                                            ref={scrollRef}
                                            sx={{
                                                overflowX: "auto",
                                                whiteSpace: "nowrap",
                                                scrollBehavior: "smooth",
                                                width: "100%",
                                                "&::-webkit-scrollbar": { display: "none" },
                                            }}
                                        >
                                            <RadioGroup row value={headerType} onChange={handleHeaderType} sx={{ flexWrap: { xs: "nowrap", sm: "wrap" }, gap: 1 }}>
                                                {headerTypes.map((option) => (
                                                    <FormControlLabel
                                                        key={option.value}
                                                        value={option.value}
                                                        control={<Radio />}
                                                        label={option.label}
                                                        sx={pillRadioSx}
                                                    />
                                                ))}
                                            </RadioGroup>
                                        </Box>
                                    </FormControl>

                                    {headerType === "1" && (
                                        <Box sx={{ mt: 2 }}>
                                            <FieldLabel>Header text</FieldLabel>
                                            <TextField
                                                fullWidth
                                                multiline
                                                minRows={1}
                                                maxRows={4}
                                                value={headText}
                                                inputRef={headTextAreaRef}
                                                placeholder="e.g. Your order is on the way"
                                                onChange={handleHeadChange}
                                                inputProps={{ maxLength: 60 }}
                                                error={headTextError}
                                            />
                                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.75, minHeight: 30 }}>
                                                {headVariableFields.length < 1 ? (
                                                    <Button
                                                        startIcon={<AddCircleIcon />}
                                                        onClick={headText === "" ? null : addHeadVariableFun}
                                                        sx={{ ...softBtnSx, ...(headText === "" ? softBtnOffSx : {}) }}
                                                    >
                                                        Add variable
                                                    </Button>
                                                ) : <span />}
                                                <Typography sx={counterSx}>{headText.length}/60</Typography>
                                            </Stack>
                                        </Box>
                                    )}

                                    {/* Header variable samples */}
                                    {headVariableFields.length > 0 && (
                                        <Box sx={sampleBoxSx}>
                                            <Typography sx={sampleTitleSx}>Samples for header content</Typography>
                                            <Typography sx={sampleTextSx}>
                                                To help us review your content, provide examples of the variables in the header. Do not include any customer information.
                                            </Typography>
                                            <Stack spacing={1.25} sx={{ mt: 1.5 }}>
                                                {headVariableFields.map((item, index) => (
                                                    <Box key={index}>
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <Box sx={varChipSx}>{item}</Box>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                placeholder={`Enter content for ${item}`}
                                                                onChange={(e) => addHeadVariableValuesFun(e, item)}
                                                                onPaste={handlePaste}
                                                                error={headTextError}
                                                                inputProps={{ maxLength: 13 }}
                                                            />
                                                        </Stack>
                                                        {sampleHeadError && (
                                                            <Typography sx={{ fontSize: 12, color: "#dc2626", mt: 0.5 }}>{sampleHeadError}</Typography>
                                                        )}
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                    )}

                                    {/* Header media */}
                                    {["2", "3", "4"].includes(headerType) && (
                                        <Box sx={{ mt: 2 }}>
                                            <FieldLabel>
                                                Sample {headerType === "2" ? "image" : headerType === "3" ? "video" : "document"}
                                            </FieldLabel>
                                            {renderInputBasedOnFileType()}
                                            <Typography sx={{ ...sampleTextSx, mt: 0.75 }}>
                                                Provide an example of the media in the header. Do not include any customer information.
                                            </Typography>
                                        </Box>
                                    )}
                                </FlowSection>

                                {/* ---------- BODY ---------- */}
                                <FlowSection
                                    icon={<NotesIcon />}
                                    title="Body"
                                    subtitle="Enter the text of your message in the language you've selected."
                                >
                                    <FieldLabel>Message text</FieldLabel>
                                    <TextField
                                        inputRef={bodyTextAreaRef}
                                        value={bodyText}
                                        onChange={handleBodyChange}
                                        placeholder="Type here... Use *Hello* for bold, _Hello_ for italic, 😊 for emoji (Win + .)"
                                        multiline
                                        minRows={4}
                                        fullWidth
                                        error={bodyError}
                                    />
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.75 }}>
                                        <Button
                                            startIcon={<AddCircleIcon />}
                                            onClick={bodyVariableCount === 11 || bodyText === "" ? null : addBodyVariableFun}
                                            sx={{ ...softBtnSx, ...(bodyVariableCount === 11 || bodyText === "" ? softBtnOffSx : {}) }}
                                        >
                                            Add variable
                                        </Button>
                                        <Typography sx={counterSx}>{bodyText.length}/1024</Typography>
                                    </Stack>

                                    {/* Body variable samples */}
                                    {bodyVariableFields.length > 0 && (
                                        <Box sx={sampleBoxSx}>
                                            <Typography sx={sampleTitleSx}>Samples for body content</Typography>
                                            <Typography sx={sampleTextSx}>
                                                To help us review your content, provide examples of the variables in the body. Do not include any customer information.
                                            </Typography>
                                            <Stack spacing={1.25} sx={{ mt: 1.5 }}>
                                                {bodyVariableFields.map((item, index) => {
                                                    const isEmpty = !bodyVariableValues[index] || bodyVariableValues[index].trim() === "";

                                                    return (
                                                        <Box key={index}>
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                <Box sx={varChipSx}>{item}</Box>
                                                                <TextField
                                                                    type="text"
                                                                    fullWidth
                                                                    size="small"
                                                                    inputProps={{ maxLength: 13 }}
                                                                    placeholder={`Enter content for ${item}`}
                                                                    value={bodyVariableValues[index]}
                                                                    error={isEmpty}
                                                                    onChange={(e) => addBodyVariableValuesFun(e, index)}
                                                                />
                                                                <Tooltip title="Delete variable" arrow>
                                                                    <IconButton onClick={() => handleDeleteVariable(index)} sx={removeBtnSx}>
                                                                        <DeleteOutlineIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Stack>
                                                            {subBodyError && (
                                                                <Typography sx={{ fontSize: 12, color: "#dc2626", mt: 0.5 }}>{subBodyError}</Typography>
                                                            )}
                                                        </Box>
                                                    )
                                                })}
                                            </Stack>
                                        </Box>
                                    )}
                                </FlowSection>

                                {/* ---------- FOOTER ---------- */}
                                <FlowSection
                                    icon={<ShortTextIcon />}
                                    title="Footer"
                                    optional
                                    subtitle="Add a short line of text to the bottom of your message."
                                >
                                    <FieldLabel>Footer text</FieldLabel>
                                    <TextField
                                        fullWidth
                                        placeholder="e.g. Reply STOP to unsubscribe"
                                        value={footerText}
                                        onChange={handleFooterChange}
                                        size="small"
                                    />
                                    <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.75 }}>
                                        <Typography sx={counterSx}>{footerText.length}/60</Typography>
                                    </Stack>
                                </FlowSection>

                                {/* ---------- BUTTONS ---------- */}
                                <FlowSection
                                    icon={<SmartButtonIcon />}
                                    title="Buttons"
                                    optional
                                    subtitle="Let customers respond to your message or take action."
                                    error={buttonError}
                                    right={
                                        <Button onClick={handleClick} startIcon={<AddCircleIcon />} sx={{ ...softBtnSx, flexShrink: 0 }}>
                                            Add Button
                                        </Button>
                                    }
                                >
                                    <Menu
                                        anchorEl={anchorEl}
                                        open={open}
                                        onClose={handleClose}
                                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                                        PaperProps={{
                                            sx: {
                                                mt: 0.75,
                                                width: 240,
                                                maxHeight: 300,
                                                borderRadius: 2.5,
                                                border: "1px solid #e9edf2",
                                                boxShadow: "0 12px 32px -12px rgba(15,23,42,0.3)",
                                            },
                                        }}
                                        MenuListProps={{
                                            // ensures click inside menu items also closes
                                            onClick: handleClose,
                                            sx: { py: 0.5 },
                                        }}
                                    >
                                        {[
                                            { option: "1", hint: "2 buttons maximum" },
                                            { option: "2", hint: "1 button maximum" },
                                            { option: "3", hint: "6 buttons maximum" },
                                        ].map(({ option, hint }, i) => {
                                            const meta = BTN_META[option];
                                            return [
                                                i > 0 && <Divider key={`d${option}`} sx={{ my: "0 !important" }} />,
                                                <MenuItem
                                                    key={option}
                                                    onClick={() => {
                                                        handleOptionClick(option);
                                                        handleCloseMenu();
                                                    }}
                                                    sx={{ py: 1, gap: 1.25 }}
                                                >
                                                    <Box sx={{ ...btnIconSx, color: meta.color, bgcolor: `${meta.color}14` }}>{meta.icon}</Box>
                                                    <Box>
                                                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#0f172a" }}>{meta.label}</Typography>
                                                        <Typography sx={{ fontSize: 11.5, color: "#94a3b8" }}>{hint}</Typography>
                                                    </Box>
                                                </MenuItem>,
                                            ];
                                        })}
                                    </Menu>

                                    {buttons.length > 0 ? (
                                        <Stack spacing={1.5}>
                                            {buttons.map((item, index) => {
                                                const meta = BTN_META[item.button_type] || BTN_META["2"];
                                                return (
                                                    <Box key={index} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2.5, border: "1px solid #e9edf2", borderLeft: `3px solid ${meta.color}`, bgcolor: "#fcfdfe" }}>
                                                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                <Box sx={{ ...btnIconSx, color: meta.color, bgcolor: `${meta.color}14` }}>{meta.icon}</Box>
                                                                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a" }}>
                                                                    {meta.label}
                                                                </Typography>
                                                            </Stack>
                                                            <Tooltip title="Remove button" arrow>
                                                                <IconButton onClick={() => handleClose(index)} sx={removeBtnSx}>
                                                                    <CloseIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Stack>

                                                        <Grid container spacing={1.5}>
                                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                                <FieldLabel>Type of action</FieldLabel>
                                                                <FormControl fullWidth size="small">
                                                                    <Select value={item.type_of_action} disabled>
                                                                        {actionTypes.map((option) => (
                                                                            <MenuItem key={option.value} value={option.value}>
                                                                                {option.label}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </FormControl>
                                                            </Grid>
                                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                                <FieldLabel>Button text</FieldLabel>
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    placeholder={item.button_type === "1" || item.button_type === "3" ? "Enter text" : "Enter"}
                                                                    value={item.button_text}
                                                                    error={!!bodyError}
                                                                    helperText={bodyError}
                                                                    name="button_name"
                                                                    onChange={(e) => handleAddButtonChange(e, index)}
                                                                />
                                                            </Grid>

                                                            {item.button_type === "1" && (
                                                                <>
                                                                    <Grid size={{ xs: 12, sm: 6 }}>
                                                                        <FieldLabel>URL type</FieldLabel>
                                                                        <FormControl fullWidth size="small">
                                                                            <Select
                                                                                value={item.url_type}
                                                                                onChange={(e) => handleHeaderType(e)}
                                                                            >
                                                                                {urlTypes.map((option) => (
                                                                                    <MenuItem key={option.value} value={option.value}>
                                                                                        {option.label}
                                                                                    </MenuItem>
                                                                                ))}
                                                                            </Select>
                                                                        </FormControl>
                                                                    </Grid>
                                                                    <Grid size={{ xs: 12, sm: 6 }}>
                                                                        <FieldLabel>Website URL</FieldLabel>
                                                                        <TextField
                                                                            fullWidth
                                                                            size="small"
                                                                            type="url"
                                                                            placeholder="https://example.com"
                                                                            value={item.website_url}
                                                                            name="website_url"
                                                                            onChange={(e) => handleAddButtonChange(e, index)}
                                                                        />
                                                                    </Grid>
                                                                </>
                                                            )}

                                                            {item.button_type !== "1" && item.button_type !== "3" && (
                                                                <>
                                                                    <Grid size={{ xs: 12, sm: 6 }}>
                                                                        <FieldLabel>Country</FieldLabel>
                                                                        <FormControl fullWidth size="small">
                                                                            <Select
                                                                                value={item.country_code}
                                                                                onChange={(e) => handleHeaderType(e)}
                                                                            >
                                                                                {countryCodes.map((option) => (
                                                                                    <MenuItem key={option.value} value={option.value}>
                                                                                        {option.label}
                                                                                    </MenuItem>
                                                                                ))}
                                                                            </Select>
                                                                        </FormControl>
                                                                    </Grid>
                                                                    <Grid size={{ xs: 12, sm: 6 }}>
                                                                        <FieldLabel>Phone number</FieldLabel>
                                                                        <TextField
                                                                            fullWidth
                                                                            size="small"
                                                                            type="text"
                                                                            placeholder="Enter Phone Number"
                                                                            value={item.phone_number}
                                                                            name="phone_number"
                                                                            inputProps={{
                                                                                inputMode: "numeric",      // show numeric keypad on mobile
                                                                                pattern: "[6-9]*",         // only digits
                                                                                maxLength: 10,             // restrict max digits
                                                                                minLength: 10
                                                                            }}
                                                                            onChange={(e) => handleAddButtonChange(e, index)}
                                                                        />
                                                                    </Grid>
                                                                </>
                                                            )}
                                                        </Grid>
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    ) : (
                                        <Typography sx={{ py: 1.5, textAlign: "center", fontSize: 13, color: "#94a3b8", border: "1px dashed #e2e8f0", borderRadius: 2.5 }}>
                                            No buttons added yet. Use “Add Button” to add one.
                                        </Typography>
                                    )}
                                </FlowSection>
                            </Stack>
                        </Grid>

                        {/* ================= RIGHT: WhatsApp preview ================= */}
                        <Grid size={{ xs: 12, md: 5, lg: 5 }}>
                            <Box sx={{ position: { md: "sticky" }, top: { md: 80 } }}>
                                <Box sx={{ borderRadius: 3, overflow: "hidden", border: "1px solid #e9edf2", boxShadow: "0 1px 3px rgba(16,24,40,0.06)" }}>
                                    {/* chat top bar */}
                                    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 1.5, py: 1, bgcolor: "#075e54", color: "#fff" }}>
                                        <Avatar sx={{ width: 34, height: 34, bgcolor: "#25d366", fontSize: 15, fontWeight: 700 }}>
                                            {(templateNameData[0]?.templateName || "T").charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>Message Preview</Typography>
                                            <Typography noWrap sx={{ fontSize: 11.5, opacity: 0.8 }}>
                                                {templateNameData[0]?.templateName || "template"}
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    {/* chat wallpaper */}
                                    <Box
                                        sx={{
                                            p: 1.5,
                                            minHeight: { xs: 220, md: 320 },
                                            bgcolor: "#efeae2",
                                            backgroundImage: `url(${convoBg})`,
                                            backgroundRepeat: "repeat",
                                            backgroundSize: "340px auto",
                                        }}
                                    >
                                        <Box sx={{ maxWidth: 330, bgcolor: "#fff", borderRadius: 2, borderTopLeftRadius: 0, boxShadow: "0 1px 1px rgba(11,20,26,0.13)", overflow: "hidden" }}>
                                            <Box sx={{ p: 0.75 }}>
                                                {/* HEADER */}
                                                <Box component="header" sx={{
                                                    overflowWrap: "break-word",
                                                    wordBreak: "break-word",
                                                    whiteSpace: "pre-wrap",
                                                    "& .med_box img, & .med_box video, & .med_box embed": { display: "block", borderRadius: "6px" },
                                                }}>

                                                    {status === "loading" && (
                                                        <CircularLoader color="success" />
                                                    )}

                                                    {headerType === "1" ? (
                                                        <Typography
                                                            sx={{ px: 0.75, pt: 0.5, fontSize: 14.5, fontWeight: 700, color: "#111b21" }}
                                                            dangerouslySetInnerHTML={processHTMLContent(headTextPreview)}
                                                        />
                                                    ) : headerType === "2" ? (
                                                        <Box className="med_box">
                                                            {selectedFile && selectedFile.length > 0 ? (
                                                                <Box
                                                                    component="img"
                                                                    src={selectedFile}
                                                                    sx={{ width: "100%", height: "auto", mb: "1px" }}
                                                                />
                                                            ) : (
                                                                <Box
                                                                    component="img"
                                                                    src="https://i.pinimg.com/1200x/ab/9e/a3/ab9ea3bd124ae5f002541a104df12258.jpg"
                                                                    sx={{ width: "100%", height: "auto", mb: "1px" }}
                                                                />
                                                            )}
                                                        </Box>
                                                    ) : headerType === "3" ? (
                                                        <Box className="med_box">
                                                            {selectedFile && selectedFile.length > 0 ? (
                                                                <video width="100%" height="auto" controls autoPlay>
                                                                    <source src={selectedFile} />
                                                                </video>
                                                            ) : (
                                                                <Box
                                                                    component="img"
                                                                    src="https://encrypted-tbn1.gstatic.com/images?q=tbn:ANd9GcR2-uOG2bLvGkyneg9YKaLBU1bDKwEqmlNAj0M4GTbM6oZg7xtr"
                                                                    alt="Video"
                                                                    sx={{
                                                                        width: "100%",
                                                                        height: "auto",
                                                                        objectFit: "cover",
                                                                        mb: "1px"
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    ) : headerType === "4" ? (
                                                        <Box className="med_box">
                                                            {selectedFile && selectedFile.length > 0 ? (
                                                                <embed
                                                                    src={`https://docs.google.com/viewer?url=${selectedFile}&embedded=true`}
                                                                    width="100%"
                                                                    height="110px"
                                                                    style={{ border: "none" }}
                                                                ></embed>
                                                            ) : (
                                                                <Box
                                                                    component="img"
                                                                    src="https://i.pinimg.com/1200x/2c/cc/21/2ccc21427e647f4ef914f789f869e8b1.jpg"
                                                                    alt="Document"
                                                                    sx={{ width: "100%", height: 116, mb: "1px" }}
                                                                />
                                                            )}
                                                        </Box>
                                                    ) : null}
                                                </Box>

                                                {/* BODY */}
                                                <Box
                                                    component="section"
                                                    sx={{
                                                        px: 0.75,
                                                        pt: 0.75,
                                                        overflowWrap: "break-word",
                                                        wordBreak: "break-word",
                                                        whiteSpace: "pre-wrap",
                                                    }}
                                                >
                                                    {bodyTextPreview ? (
                                                        <Typography
                                                            sx={{ fontSize: 13.5, lineHeight: 1.5, color: "#111b21" }}
                                                            dangerouslySetInnerHTML={processHTMLContent(bodyTextPreview)}
                                                        />
                                                    ) : (
                                                        <Typography sx={{ fontSize: 13, color: "#94a3b8", fontStyle: "italic" }}>
                                                            Your message body will appear here…
                                                        </Typography>
                                                    )}
                                                </Box>

                                                {/* FOOTER */}
                                                {footerText && (
                                                    <Box component="footer" sx={{ px: 0.75, pt: 0.5 }}>
                                                        <Typography sx={{ fontSize: 12, color: "#667781", wordBreak: "break-word" }}>
                                                            {footerText}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                <Typography sx={{ px: 0.75, textAlign: "right", fontSize: 11, color: "#667781" }}>
                                                    {previewTime}
                                                </Typography>
                                            </Box>

                                            {/* BUTTONS */}
                                            {buttons.length > 0 && (
                                                <Stack>
                                                    {buttons.map((item, index) => {
                                                        const meta = BTN_META[item.button_type] || BTN_META["2"];
                                                        return (
                                                            <Stack
                                                                key={index}
                                                                direction="row"
                                                                alignItems="center"
                                                                justifyContent="center"
                                                                spacing={0.75}
                                                                sx={{ py: 1, px: 1, borderTop: "1px solid #eef0f2", color: "#027eb5", "& svg": { fontSize: 16 } }}
                                                            >
                                                                {meta.icon}
                                                                <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600 }}>{item.button_text}</Typography>
                                                            </Stack>
                                                        );
                                                    })}
                                                </Stack>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>

                <Box sx={stepActionsSx}>
                    <Stack direction="row" spacing={1.5}>
                        <Button disabled={activeStep === 0} onClick={handleBack} sx={backBtnSx}>
                            Back
                        </Button>

                        <Box sx={{ cursor: loader === "loading" ? "not-allowed" : "pointer" }}>
                            <Button
                                disabled={loader === "loading"}
                                onClick={loader === "loading" ? null : handleTemplateSubmit}
                                sx={nextBtnSx}
                            >
                                {loader === "loading" && (
                                    <CircularProgress size={15} sx={{ mr: 1, color: "#fff" }} />
                                )}
                                {loader === "loading" ? "Submitting.." : "Submit"}
                            </Button>
                        </Box>
                    </Stack>
                </Box>
            </Card>

            <SnackbarMessage
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            />
        </Box>
    );
}